import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import { useEffect, useMemo, useState } from "react";
import WorkOrderCard from "components/QueueBoard/work-order-dashboard-card/WorkOrderDetail";
import { BoardView } from "../types/boardView";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { UpdateWorkOrder, WorkOrdersByStatus } from "../models/workOrderTypes";
import {
  convertToRGBA,
  truncateName,
} from "../../common/Utils/commonUtilityFunctions";
import WorkOrderDialogContainer from "components/workOrder/WorkOrderDialogContainerMain";
import { WorkOrderQueueAPI } from "../api/workOrderApi";
import LinearProgress from "@mui/material/LinearProgress";
import { useBoardFilters, useBoardLoading, useStatusBoardData } from "../../store/zustandWorkOrderStore";
import { useBoardColumns } from "../shared/hooks/useBoardColumns";
import { useCachedWorkOrderOpen } from "../shared/hooks/useCachedWorkOrderOpen";
import StatusBoardCustomColumn from "./StatusBoardCustomColumns";
import packageJson from "../../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "../../authentication/models/authTypes";
import localStorageHelper from "../../../app/localStorage";
import { useEventTimer } from "components/common/amplitude/useEventTimer";
import {
  filterColumnsWithWorkOrders,
  getVisibleColumnEntries,
  parseDroppableColumnIndex,
  updateColumnWorkOrders,
  updateTwoColumnWorkOrders,
} from "../shared/boardColumnUtils";
import { ConcurrencyError } from "../../common/Utils/concurrencyError";
import { ConcurrencyDialog } from "../../common/components/ConcurrencyDialog";

const StatusBoard = () => {
  const theme = useTheme();
  const loading = useBoardLoading();
  const WorkOrdersByStatus = useStatusBoardData();
  const filters = useBoardFilters();
  const [columns, setColumns] = useBoardColumns<WorkOrdersByStatus>(
    WorkOrdersByStatus,
    "statusPosition",
  );
  const { showDialog, selectedWorkOrderId, showHideWorkOrderPopup } =
    useCachedWorkOrderOpen({
      loading,
      hasData: (WorkOrdersByStatus?.length ?? 0) > 0,
    });
  const { startTimer, completeTimer } = useEventTimer();

  // Add state to track the recently dropped work order
  const [recentlyDroppedWorkOrder, setRecentlyDroppedWorkOrder] = useState<
    number | null
  >(null);

  // Concurrency dialog state
  const [showConcurrencyDialog, setShowConcurrencyDialog] = useState(false);
  const [concurrencyMessage, setConcurrencyMessage] = useState("");

  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user"),
  );
  const [userName] = useState<string | "">(
    localStorage.getItem("username") || "",
  );

  // Clear the flashing effect after animation completes
  useEffect(() => {
    if (recentlyDroppedWorkOrder !== null) {
      const timer = setTimeout(() => {
        setRecentlyDroppedWorkOrder(null);
      }, 3000); // 3 seconds to match the CSS animation duration

      return () => clearTimeout(timer);
    }
  }, [recentlyDroppedWorkOrder]);

  // Refresh logic moved to FilterPanel.tsx

  // // Cleanup effect to clear cache when component unmounts or when dialog closes
  // useEffect(() => {
  //   return () => {
  //     // Only clear cache if we're not showing a dialog (to prevent clearing during work order opening)
  //     if (!showDialog) {
  //       localStorage.removeItem("cachedNewWorkOrderId");
  //       localStorage.removeItem("cachedNewWorkOrderDocumentNumber");
  //     }
  //   };
  // }, [showDialog]);

  const [showConfig, setShowConfig] = useState(false);
  const handleSaveColumnOrder = (updatedColumns: WorkOrdersByStatus[]) => {
    setColumns(updatedColumns);
    setShowConfig(false);
  };

  // Get zoom level from filters (set in FilterPanel)
  const zoomLevel = filters?.boardZoom ?? 1.0;
  const visibleColumns = useMemo(
    () => filterColumnsWithWorkOrders(columns),
    [columns],
  );
  const visibleColumnEntries = useMemo(
    () => getVisibleColumnEntries(columns),
    [columns],
  );

  const showHideWorkOrderPopupHandler = (isPopupToShow: boolean) => {
    const timer = startTimer(
      (isPopupToShow ? "open" : "close") + "showHideWorkOrderPopup",
      "Wo status Board",
      "status-board.tsx",
    );

    showHideWorkOrderPopup(isPopupToShow);

    completeTimer(
      (isPopupToShow ? "open" : "close") + "showHideWorkOrderPopup",
    );
  };
  const onDragEnd = async (
    result: DropResult,
    boardColumns: WorkOrdersByStatus[],
    setBoardColumns: React.Dispatch<
      React.SetStateAction<WorkOrdersByStatus[]>
    >,
  ) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const sourceIndex = parseDroppableColumnIndex(source.droppableId);
    const destIndex = parseDroppableColumnIndex(destination.droppableId);
    const sourceColumn = boardColumns[sourceIndex];
    const destColumn = boardColumns[destIndex];

    if (!sourceColumn || !destColumn) return;

    if (sourceIndex !== destIndex) {
      const sourceItems = [...sourceColumn.workOrders];
      const destItems = [...destColumn.workOrders];
      const [removed] = sourceItems.splice(source.index, 1);
      const updatedRemoved = {
        ...removed,
        workOrderStatusIdentity: destColumn.identity,
        WorkOrderStatusIdentity: destColumn.identity,
      };

      destItems.splice(destination.index, 0, updatedRemoved);

      const updatedSourceItems = sourceItems.map((item, index) => ({
        ...item,
        position: index,
        statusPosition: index,
      }));
      const updatedDestItems = destItems.map((item, index) => ({
        ...item,
        position: index,
        statusPosition: index,
      }));

      setBoardColumns(
        updateTwoColumnWorkOrders(
          boardColumns,
          sourceIndex,
          updatedSourceItems,
          destIndex,
          updatedDestItems,
        ),
      );

      setRecentlyDroppedWorkOrder(Number.parseInt(result.draggableId, 10));

      const updatedWO: UpdateWorkOrder = {
        invoiceDocumentIdentity: updatedRemoved.invoiceDocumentIdentity,
        WorkOrderStatusIdentity: destColumn.identity,
        modifiedDate: new Date().toISOString(),
        rowVersion: removed.rowVersion,
      };

      try {
        const statusUpdated =
          await WorkOrderQueueAPI.updateWorkOrderStatus(updatedWO);
        if (!statusUpdated) {
          throw new Error("Failed to update work order status.");
        }
      } catch (error) {
        if (error instanceof ConcurrencyError) {
          setConcurrencyMessage(
            "The work order has been updated by some other user, please refresh the board and try again!",
          );
          setShowConcurrencyDialog(true);
          return;
        }
        throw error;
      }

      await WorkOrderQueueAPI.updateWorkOrdersstatusPositions({
        sourceItems: updatedSourceItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
        destItems: updatedDestItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
      });
    } else {
      const copiedItems = [...sourceColumn.workOrders];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);

      const updatedItems = copiedItems.map((workOrder, index) => ({
        ...workOrder,
        position: index,
        statusPosition: index,
      }));

      setBoardColumns(
        updateColumnWorkOrders(boardColumns, sourceIndex, updatedItems),
      );

      setRecentlyDroppedWorkOrder(Number.parseInt(result.draggableId, 10));

      await WorkOrderQueueAPI.updateWorkOrdersstatusPositions({
        items: updatedItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
      });
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "onDragEndStatusBoard",
      {
        elapsed_ms: "124",
        title: "Wo status Board",
        componentName: "status-board.tsx",
        version: packageJson.version,
      },
    );
  };

  const addNewWorkOrder = (statusIdentity: number) => {
    localStorage.setItem(`${"IsStatusView"}_${userName}`, "true");
    localStorage.setItem(`${"IsTechView"}_${userName}`, "");
    localStorage.setItem("statusIdentity", statusIdentity.toString());
    showHideWorkOrderPopup(true);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "addNewWorkOrderStatusBoard",
      {
        elapsed_ms: "124",
        title: "Wo status Board",
        componentName: "status-board.tsx",
        version: packageJson.version,
      },
    );
  };

  return (
    <Box
      sx={{
        overflow: showConfig ? "visible" : "auto",
        height: "100%",
        backgroundColor: theme.palette.mode === "light"
          ? "#f5f5f5"
          : "#1e1e1e",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          minHeight: "100%",
          padding: 2,
        }}
      >
        {showConfig ? (
          <StatusBoardCustomColumn
            columns={
              Array.isArray(columns) ? columns : Object.values(columns ?? {})
            }
            onSave={handleSaveColumnOrder}
            onCancel={() => setShowConfig(false)}
          />
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <DragDropContext
                key="worker1"
                onDragEnd={(result) => {
                  onDragEnd(result, columns, setColumns);
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: zoomLevel,
                    width: "100%",
                    minHeight: "100%",
                    alignItems: "stretch",
                  }}
                >
                  {visibleColumnEntries.map(([columnId, column]) => {
                      return (
                        <Droppable
                          key={column.identity ?? columnId}
                          droppableId={columnId}
                        >
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              key={columnId}
                              sx={{
                                flexGrow: 1,
                                flexBasis:
                                  visibleColumns.length > 4
                                    ? "0"
                                    : "auto",
                                flexShrink: 0,
                                minWidth:
                                  visibleColumns.length > 4
                                    ? `${300 * zoomLevel}px`
                                    : "0px",
                                maxWidth: `${300 * zoomLevel}px`,
                                minHeight: `${120 * zoomLevel}px`,
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <Card
                                elevation={0}
                                sx={{
                                  borderRadius: 2,
                                  bgcolor:
                                    theme.palette.mode === "light"
                                      ? "#ebeced"
                                      : "#2d2d2d",
                                  borderTop: `${
                                    8 * zoomLevel
                                  }px solid ${convertToRGBA(
                                    column.statusColor,
                                  )}`,
                                  borderTopLeftRadius: `${12 * zoomLevel}px`,
                                  borderTopRightRadius: `${12 * zoomLevel}px`,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                  overflow: "visible",
                                  height: "100%",
                                }}
                              >
                                <CardContent
                                  className="status"
                                  sx={{
                                    padding: `${9 * zoomLevel}px`,
                                    textAlign: "right",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    backgroundColor:
                                      theme.palette.mode === "light"
                                        ? "#ebeced"
                                        : "#2d2d2d",
                                    borderRadius: `${12 * zoomLevel}px ${12 * zoomLevel}px 0 0`,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    sx={{
                                      borderRadius: `${3 * zoomLevel}px`,
                                      p: 0.3 * zoomLevel,
                                    }}
                                  >
                                    <Chip
                                      label=""
                                      size="small"
                                      sx={{
                                        bgcolor: convertToRGBA(
                                          column.statusColor,
                                        ),
                                        color: `${column.textColor}`,
                                        mt: `${3 * zoomLevel}px`,
                                        mr: `${5 * zoomLevel}px`,
                                        width: `${12 * zoomLevel}px`,
                                        height: `${12 * zoomLevel}px`,
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontStyle: "normal",
                                        fontWeight: "500",
                                        fontSize: `${15 * zoomLevel}px`,
                                        color: theme.palette.text.primary,
                                      }}
                                    >
                                      {truncateName(column.displayText, 25)}
                                    </Typography>
                                    <Box flexGrow={1} />

                                    <Tooltip title="Customize the column order">
                                      <IconButton
                                        onClick={() => setShowConfig(true)}
                                        sx={{
                                          marginLeft: `${3 * zoomLevel}px`,
                                          padding: `${2 * zoomLevel}px`,
                                          width: `${28 * zoomLevel}px`,
                                          height: `${28 * zoomLevel}px`,
                                          color: "#2371E7",
                                          bgcolor: "#ffffff",
                                          borderRadius: `${3 * zoomLevel}px`,
                                          "&:hover": {
                                            bgcolor: theme.palette.mode === "light"
                                              ? "rgba(0, 0, 0, 0.04)"
                                              : "rgba(255, 255, 255, 0.08)",
                                          },
                                        }}
                                        size="small"
                                      >
                                        <Edit
                                          sx={{
                                            width: `${16 * zoomLevel}px`,
                                            height: `${16 * zoomLevel}px`,
                                            fontSize: `${15 * zoomLevel}px`,
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Add new work order">
                                      <IconButton
                                        onClick={() =>
                                          addNewWorkOrder(column.identity)
                                        }
                                        sx={{
                                          marginLeft: `${3 * zoomLevel}px`,
                                          padding: `${2 * zoomLevel}px`,
                                          width: `${28 * zoomLevel}px`,
                                          height: `${28 * zoomLevel}px`,
                                          color: "#2371E7",
                                          bgcolor: "#ffffff",
                                          borderRadius: `${3 * zoomLevel}px`,
                                          "&:hover": {
                                            bgcolor: theme.palette.mode === "light"
                                              ? "rgba(0, 0, 0, 0.04)"
                                              : "rgba(255, 255, 255, 0.08)",
                                          },
                                        }}
                                        size="small"
                                      >
                                        <AddIcon
                                          sx={{
                                            width: `${16 * zoomLevel}px`,
                                            height: `${16 * zoomLevel}px`,
                                            fontSize: `${15 * zoomLevel}px`,
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  <Box
                                     sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 2,
                                      backgroundColor: theme.palette.mode === "light"
                                        ? "#fafafa"
                                        : "rgba(255, 255, 255, 0.05)",
                                      borderRadius: 1,
                                      padding: `${6 * zoomLevel}px ${12 * zoomLevel}px`,
                                      mt: `${8 * zoomLevel}px`,
                                      border: theme.palette.mode === "light"
                                        ? "1px solid #eeeeee"
                                        : "1px solid rgba(255, 255, 255, 0.1)",
                                        boxShadow: theme.palette.mode === "light"
                                        ? "0 2px 8px rgba(0, 0, 0, 0.06)"
                                        : "0 2px 8px rgba(0, 0, 0, 0.2)",
                                    }}
                                  >
                                   <Box sx={{ textAlign: "center" }}>
                                      {/* First Row */}

                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: `${10 * zoomLevel}px`,
                                          color: theme.palette.text.secondary,
                                          mr: 0.5,
                                        }}
                                      >
                                        Quoted:<span> </span>
                                      </Typography>
                                      <Typography component="span"
                                        sx={{
                                          fontSize: `${11 * zoomLevel}px`,
                                          fontWeight: 700,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {column.elapsedHours?.toFixed(2) ??
                                          "0.00"}{" "}
                                        Hrs
                                      </Typography>
                                    </Box>
                                    {/* Separator Line */}

                                    {/* Second Row */}
                                    <Box
                                      sx={{
                                        width: "1px",
                                        height: `${14 * zoomLevel}px`,
                                        backgroundColor: theme.palette.divider,
                                      }}
                                    />
                                      <Box sx={{ textAlign: "center" }}>
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: `${10 * zoomLevel}px`,
                                          color: theme.palette.text.secondary,
                                          mr: 0.5,
                                        }}
                                      >
                                        Clocked:<span> </span>
                                      </Typography>
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: `${11 * zoomLevel}px`,
                                          fontWeight: 700,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {column.hoursClocked?.toFixed(2) ??
                                          "0.00"}{" "}
                                        Hrs
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>

                                {column.workOrders.map((workOrder, idx) => (
                                  <Draggable
                                    key={workOrder.invoiceDocumentIdentity}
                                    draggableId={String(
                                      workOrder.invoiceDocumentIdentity,
                                    )}
                                    index={idx}
                                  >
                                    {(provided, snapshot) => (
                                      <Box
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        ml={zoomLevel}
                                        mr={zoomLevel}
                                        p={0.5 * zoomLevel}
                                        sx={{
                                          cursor: snapshot.isDragging
                                            ? "grabbing"
                                            : "grab",
                                        }}
                                      >
                                        <WorkOrderCard
                                          index={idx}
                                          color={column.statusColor}
                                          boardView={BoardView.Status}
                                          workOrderObj={workOrder}
                                          recentlyDroppedWorkOrder={
                                            recentlyDroppedWorkOrder ||
                                            undefined
                                          }
                                          isCollapsed={
                                            filters?.collapseWorkOrders ?? false
                                          }
                                        />
                                      </Box>
                                    )}
                                  </Draggable>
                                ))}
                              </Card>
                              {provided.placeholder}
                            </Box>
                          )}
                        </Droppable>
                      );
                    })}
                </Box>
              </DragDropContext>
            )}
            {showDialog && (
              <WorkOrderDialogContainer
                showHideWoPopup={showHideWorkOrderPopupHandler}
                InvoiceDocIdentity={selectedWorkOrderId || undefined}
                open={true}
                cacheNewWorkOrderIdOnSave
              />
            )}

            {/* Concurrency Conflict Dialog */}
            <ConcurrencyDialog
              open={showConcurrencyDialog}
              onDismiss={() => {
                setShowConcurrencyDialog(false);
              }}
              onRefresh={() => {
                setShowConcurrencyDialog(false);
                window.dispatchEvent(new CustomEvent("refreshWorkOrders"));
              }}
              message={concurrencyMessage}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default StatusBoard;
//WorkOrdersBystatus
